import { CheckChunky } from '@eventbrite/eds-iconography';
import { LoadingOverlay } from '@eventbrite/eds-loading-overlay';
import { PaneLayoutInteractionType } from '@eventbrite/eds-modal';
import {
    TYPE_ERROR,
    TYPE_NEUTRAL,
    TYPE_SUCCESS,
} from '@eventbrite/eds-notification';
import { formValidate } from '@eventbrite/eds-utils';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import { UrgencySignalsProps } from '@eventbrite/urgency-signals';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { FooterError } from '../../components/FooterError';
import { FullPageLoadingLayer } from '../../components/fullPageLoadingLayer/FullPageLoadingLayer';
import LanguageAndBrand from '../../components/languageAndBrand';
import { LimitedPaymentMethodsInMobileNotification } from '../../components/LimitedPaymentMethodsInMobileNotification';
import {
    PromoCodeHandlers,
    PromoCodeOptions,
} from '../../components/promoCode/constants';
import PromoCodeBar from '../../components/promoCode/PromoCodeBar';
import ResellerFooter from '../../components/resellerFooter/ResellerFooter';
import { TicketCommonProps } from '../../components/ticketsSelectionList/constants';
import {
    default as TicketCategoryGroup,
    TicketCategoryGroupProps,
} from '../../components/ticketsSelectionList/TicketCategoryGroup/TicketCategoryGroup';
import { UrgencySignalsNode } from '../../components/urgencySignals/UrgencySignalsNode';
import {
    CTA_JOIN_WAITLIST,
    TicketClassCategory,
    TICKETS_FULL_SIZE_PATH,
    TICKETS_PAGE_FORM_NAME,
} from '../../constants';
import { FOCUSABLE_FORM_ELEMENTS } from '../../constants/focusableElements';
import { STATSIG_EVENT_NAMES } from '../../constants/statsig';
import { GroupType } from '../../models/groupRegistration';
import { Ticket, TicketReseller } from '../../models/tickets';
import RebrandingAlertCircleIcon from '../../resources/rebranding-alert-circle-icon';
import { resetFocusToInitialElement } from '../../utils/accessibility';
import { getPaneInteractionType } from '../../utils/getPaneInteractionType';
import { useIsGooglePayOnTicketSelectionExperimentEnabled } from '../../utils/hooks/statsig';
import { getFooterHeight, getLoadingOverlayStyle } from '../../utils/rebrand';
import { NEWLY_GROUP_CREATED_TEXT } from '../groupRegistration/terminology';
import { PageTemplate } from '../template';
import { getTemplateHeaderProps } from '../template/utils';
import * as constants from './constants';
import {
    allTicketsFree,
    getTotalSelectedTickets,
    shouldShowEventReseller,
} from './utils/ticketUtils';
import validate from './validation';
import WaitingRoomCTA from './WaitingRoomCTA';

import ConnectedExpressCheckout from '../../components/ExpressCheckout/ConnectedExpressCheckout';
import styles from './TicketSelectionFullSizePage.module.scss';

type onQuantityChangeFunction = TicketCategoryGroupProps['onQuantityChange'];
type onQuantityChangeFunctionParam = Parameters<onQuantityChangeFunction>[0];

export interface TicketSelectionFullSizePageProps {
    areExpressCheckoutConditionsReady: boolean;
    ctaText: GenericLazyString | string;
    errors: any;
    eventDate: string;
    eventTitle: GenericLazyString | string;
    isAppRebrandActive: boolean;
    isDisabled: boolean;
    isDraftEvent: boolean;
    isExpressApplePayEnabled: boolean;
    isExpressGooglePayEnabled: boolean;
    isExpressPayPalEnabled: boolean;
    isFormSubmitting: boolean;
    isMakingApplePayPayment: boolean;
    isMakingGooglePayPayment: boolean;
    isMakingPaypalPayment: boolean;
    isModal: boolean;
    isThereGooglePayPaymentRequest?: boolean;
    numWaitingRoomVisits?: number;
    onDonationChange: () => void;
    onJoinWaitlist: () => void;
    onQuantityChange: onQuantityChangeFunction;
    onPageFullyLoaded: () => void;
    onTicketSelectionLayoutStabilized: () => void;
    refreshGooglePayPaymentRequest?: () => void;
    resetPaypalScriptStatus: () => void;
    shouldHideStatusOnTicket?: boolean;
    selectedTickets: Ticket[];
    ticketReseller: TicketReseller;
    urgencySignals: UrgencySignalsProps;

    /**
     * Group type selected
     */
    groupType: GroupType;
    /**
     * Used for Google Analytics tracking for cart summary icon in footer
     */
    onCartIconClick: (isPaneOpen: boolean) => void;
    /**
     * A common sales start or end date for all tickets, if it exists
     */
    salesDateForAllTickets: GenericLazyString | string;
    /**
     * Whether to show the headings for ticket class category groupings
     */
    shouldShowTicketCategoryGroupHeaders: boolean;
    /**
     * Group Registration event props used to show a notification when a new group has just been created
     */
    showNewlyCreatedGroupNotification?: boolean;
    /**
     * A sales status (sales end soon, sales not started, etc.) that applies to all tickets, if it exists
     */
    statusForAllTickets?: string;
    /**
     * Props related to the checkout template
     */
    templateProps: {
        isReservedSeating: boolean;
        isSubmitting: boolean;
        totalCostDisplay: GenericLazyString | string;
        totalTicketQuantity: number;
    };
    /**
     * An array of tickets for this event.
     */
    tickets: Array<TicketCommonProps>;
    /**
     * Ticket classes sorted by category
     */
    ticketClassesByCategory: Record<string, TicketCategoryGroupProps>;
    /**
     * An array of tickets with promo code applied.
     */
    ticketsWithAppliedCode: Array<TicketCommonProps>;
    /**
     * Node displaying any validation error next to the submit button.
     * Note that this node will be rendered only when the form has been submitted at least once.
     */
    validationErrorMessage: string | GenericLazyString | null;
    isRegEvent: boolean;

    /**
     * Props passed by redux-form
     */
    error: GenericLazyString | string;
    handleSubmit: (
        callback: (values: Record<string, unknown>) => void,
    ) => () => void;
    submitFailed: boolean;
    /**
     * PromoCodeForm-related props
     */
    shouldShowPromoCodeForm: boolean;
    shouldShowWaitingRoomCTA: boolean;
    submitPromoCodeFormFailed: boolean;
    promoCodeFormHandlers: PromoCodeHandlers;
    promoCodeFormOptions: PromoCodeOptions;
    onPageWillMount: (shouldShowPromoCodeForm: boolean) => void;
    onSubmit: (values: Record<string, unknown>) => void;
    /*
     * Series event props
     */
    onBackToSeries: () => void;
    shouldShowBackToSeriesLink: boolean;
    /**
     * Group Registration event props used to determine and
     * display back button to return to group selection page
     */
    hasAdvancedTeamsEnabled: boolean;
    onBackToGroupTypeSelection: () => void;
}

export const TicketSelectionFullSizePage: FunctionComponent<
    TicketSelectionFullSizePageProps & {
        expandedTicketCount: number;
        hasSubmittedForm: boolean;
        isClientSideRendered: boolean;
        isPaneOpen: boolean;
        isLoading: boolean;
        isSeriesInfiniteCalendar: boolean;
    }
> = ({
    areExpressCheckoutConditionsReady,
    ctaText,
    error,
    errors,
    eventDate,
    eventTitle,
    groupType,
    handleSubmit,
    hasAdvancedTeamsEnabled,
    isSeriesInfiniteCalendar,
    isDisabled,
    isAppRebrandActive,
    isDraftEvent,
    isExpressApplePayEnabled,
    isExpressGooglePayEnabled,
    isExpressPayPalEnabled,
    isFormSubmitting,
    isMakingApplePayPayment,
    isMakingGooglePayPayment,
    isMakingPaypalPayment,
    isModal,
    isRegEvent,
    isThereGooglePayPaymentRequest,
    numWaitingRoomVisits,
    onBackToGroupTypeSelection,
    onBackToSeries,
    onCartIconClick,
    onDonationChange,
    onJoinWaitlist,
    onPageFullyLoaded,
    onPageWillMount,
    onQuantityChange,
    onSubmit,
    onTicketSelectionLayoutStabilized,
    promoCodeFormHandlers,
    promoCodeFormOptions,
    refreshGooglePayPaymentRequest,
    resetPaypalScriptStatus,
    salesDateForAllTickets,
    selectedTickets,
    shouldHideStatusOnTicket,
    shouldShowBackToSeriesLink,
    shouldShowPromoCodeForm,
    shouldShowTicketCategoryGroupHeaders,
    shouldShowWaitingRoomCTA,
    showNewlyCreatedGroupNotification,
    statusForAllTickets,
    submitFailed,
    submitPromoCodeFormFailed,
    templateProps,
    ticketClassesByCategory,
    ticketReseller,
    tickets,
    ticketsWithAppliedCode,
    urgencySignals,
    validationErrorMessage,
}) => {
    const focusRef = React.createRef<HTMLDivElement>();

    const [isPaneOpen, setIsPaneOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSubmittedForm, setHasSubmittedForm] = useState(false);
    const [isExpressCheckoutStabilized, setIsExpressCheckoutStabilized] =
        useState<boolean>(false);
    const [hasCalledBack, setHasCalledBack] = useState<boolean>(false);

    const defaultPaneInteraction =
        isAppRebrandActive || isSeriesInfiniteCalendar
            ? PaneLayoutInteractionType.asModal
            : PaneLayoutInteractionType.asCollapsible;
    const [paneInteraction, setPaneInteraction] = useState(
        defaultPaneInteraction,
    );

    const prevPromoCode =
        useRef<
            TicketSelectionFullSizePageProps['promoCodeFormOptions']['promoCode']
        >('');

    useEffect(() => {
        if (promoCodeFormOptions.promoCode !== prevPromoCode.current) {
            const promoAdded =
                !prevPromoCode.current && promoCodeFormOptions.promoCode;
            const promoRemoved =
                prevPromoCode.current && !promoCodeFormOptions.promoCode;

            if (promoAdded || promoRemoved) {
                setIsLoading(false);
            }

            prevPromoCode.current = promoCodeFormOptions.promoCode;
        }

        if (submitPromoCodeFormFailed) {
            setIsLoading(false);
        }
    }, [isLoading, promoCodeFormOptions.promoCode, submitPromoCodeFormFailed]);

    useEffect(() => {
        onPageFullyLoaded();

        onPageWillMount(shouldShowPromoCodeForm);

        if (isModal && focusRef.current) {
            resetFocusToInitialElement({
                rootEl: focusRef.current,
                focusableEls: FOCUSABLE_FORM_ELEMENTS,
            });
            focusRef.current.scrollIntoView(false);
        }

        return () => {
            resetPaypalScriptStatus();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (submitFailed && error) {
            setIsPaneOpen(false);
        }
    }, [error, submitFailed]);

    const _onPaneToggle = () => {
        onCartIconClick(isPaneOpen);
        setIsPaneOpen(!isPaneOpen);
    };

    const _onSubmitTracking = (data = { statsigValue: null }) => {
        const { statsigValue } = data;

        logEvent(
            STATSIG_EVENT_NAMES.TICKET_STEP_CONTINUE_TO_PAYMENT_CLICK,
            statsigValue,
        );

        if (!hasSubmittedForm && validationErrorMessage) {
            logEvent(STATSIG_EVENT_NAMES.TICKET_STEP_VALIDATION_ERROR_VIEW);
        }
    };

    const _onExpressPaymentSubmit = (data = { statsigValue: null }) => {
        _onSubmitTracking(data);
        setHasSubmittedForm(true);
    };

    const _onSubmit = (data = { statsigValue: null }) => {
        _onSubmitTracking(data);
        setHasSubmittedForm(true);

        if (validationErrorMessage && focusRef.current) {
            resetFocusToInitialElement({
                rootEl: focusRef.current,
                focusableEls: FOCUSABLE_FORM_ELEMENTS,
            });
            focusRef.current.scrollIntoView(false);
            return;
        }

        handleSubmit((values) =>
            onSubmit({
                ...values,
                ...data,
            }),
        )();
    };

    const _handleNotificationClose = () => {
        const { onClear } = promoCodeFormHandlers;
        const { promoCode } = promoCodeFormOptions;

        // Show loading spinner during removal of applied promo code
        setIsLoading(true);
        onClear(promoCode, undefined, undefined, isAppRebrandActive);
    };

    const _handleSubmitPromoCode = (e: string) => {
        const { onSubmit } = promoCodeFormHandlers;

        setIsLoading(true);

        onSubmit(e);
    };

    const _getNotificationOptions = () => {
        const submitError = error;
        const notificationOptions = [];
        const errorIconType = isAppRebrandActive ? (
            <RebrandingAlertCircleIcon />
        ) : null;

        if (!isEmpty(errors)) {
            // Currently handles waitlist and promo code errors on initial load
            errors.forEach((error: $FixMe) => {
                const errorDescription = error.errorDescription;

                notificationOptions.push({
                    type: TYPE_ERROR,
                    children: errorDescription,
                    isAppRebrandActive,
                    iconType: errorIconType,
                });
            });
        }

        if (submitFailed && submitError) {
            notificationOptions.push({
                type: TYPE_ERROR,
                children: submitError,
                isAppRebrandActive,
                iconType: errorIconType,
            });
        }

        if (isDraftEvent) {
            notificationOptions.push({
                type: TYPE_NEUTRAL,
                children: constants.EVENT_IN_DRAFT_TEXT,
                isAppRebrandActive,
            });
        }

        if (showNewlyCreatedGroupNotification) {
            notificationOptions.push({
                type: TYPE_SUCCESS,
                iconType: <CheckChunky />,
                hasCloseButton: true,
                children: (
                    <span data-spec="newly-created-group-text">
                        {NEWLY_GROUP_CREATED_TEXT[groupType]}
                    </span>
                ),
            });
        }

        return notificationOptions;
    };

    const shouldHideFooter =
        statusForAllTickets &&
        [constants.SALES_NOT_STARTED, constants.SALES_START_SOON].includes(
            statusForAllTickets,
        );

    // Setting ctaText to null will make the footer not display
    const fixedFooterCtaText = (!shouldHideFooter && ctaText) || null;

    const showEventReseller = shouldShowEventReseller(ticketReseller);

    const extraPaneProps = {
        isTicketSelectionPage: true,
        buyOnMapPaneContent: null,
        showCartIconInPane: false,
        isSeriesInfiniteCalendar,
        areTicketsSelected: getTotalSelectedTickets(selectedTickets || []) > 0,
        areOnlyFreeTicketsSelected:
            getTotalSelectedTickets(selectedTickets || []) > 0 &&
            allTicketsFree(selectedTickets || []),
    };
    const ticketsWrapperClassNames = classNames(
        'eds-l-sn-mar-top-0 eds-l-sm-mar-top-0 ',
        'eds-l-mar-top-2 eds-l-md-mar-top-2 ',
        'eds-l-mw-mar-top-8 eds-l-ln-mar-top-8 eds-l-lg-mar-top-8 eds-l-lw-mar-top-8',
        { 'eds-l-mar-bot-8': isAppRebrandActive },
    );
    const promoCodeWrapperClassNames = classNames(
        'eds-l-sn-mar-top-6 eds-l-sm-mar-top-6 eds-l-sw-mar-top-6 eds-l-mar-top-8',
    );

    const ticketClassCategories = Object.keys(ticketClassesByCategory);

    const footerMessageNode =
        hasSubmittedForm && validationErrorMessage ? (
            <FooterError isAppRebrandActive={isAppRebrandActive}>
                {validationErrorMessage}
            </FooterError>
        ) : null;

    const isGooglePayOnTicketSelectionExperimentEnabled =
        useIsGooglePayOnTicketSelectionExperimentEnabled();

    const allowGooglePay =
        isExpressGooglePayEnabled &&
        isGooglePayOnTicketSelectionExperimentEnabled;

    const isExpressCheckout =
        isExpressApplePayEnabled || isExpressPayPalEnabled || allowGooglePay;

    const shouldRenderExpressCheckoutButtons =
        !shouldHideFooter && isExpressCheckout;

    useEffect(() => {
        if (!areExpressCheckoutConditionsReady) {
            return;
        }

        if (shouldRenderExpressCheckoutButtons) {
            return;
        }

        if (hasCalledBack) {
            return;
        }

        onTicketSelectionLayoutStabilized();
        setHasCalledBack(true);
    }, [
        areExpressCheckoutConditionsReady,
        onTicketSelectionLayoutStabilized,
        shouldRenderExpressCheckoutButtons,
        hasCalledBack,
    ]);

    useEffect(() => {
        if (!isExpressCheckoutStabilized) {
            return;
        }

        if (hasCalledBack) {
            return;
        }

        onTicketSelectionLayoutStabilized();
        setHasCalledBack(true);
    }, [
        hasCalledBack,
        isExpressCheckoutStabilized,
        onTicketSelectionLayoutStabilized,
    ]);

    useEffect(() => {
        const paneInteraction =
            isAppRebrandActive || isSeriesInfiniteCalendar
                ? getPaneInteractionType()
                : PaneLayoutInteractionType.asCollapsible;

        setPaneInteraction(paneInteraction);
    }, [isAppRebrandActive, selectedTickets, isSeriesInfiniteCalendar]);

    const modalFooterNode = shouldRenderExpressCheckoutButtons ? (
        <ConnectedExpressCheckout
            handleSubmit={_onSubmit}
            handleExpressPaymentSubmit={_onExpressPaymentSubmit}
            isSubmitting={templateProps.isSubmitting}
            onFullyLoaded={() => {
                setIsExpressCheckoutStabilized(true);
            }}
        />
    ) : null;

    const fullPageLoadingLayer = isExpressCheckout ? (
        <FullPageLoadingLayer
            isAppRebrandActive={isAppRebrandActive}
            isShown={
                templateProps.isSubmitting &&
                (isMakingApplePayPayment ||
                    isMakingPaypalPayment ||
                    isMakingGooglePayPayment)
            }
        >
            <div className="eds-text-bl eds-mar-top-5">
                {gettext('Placing order')}
            </div>
        </FullPageLoadingLayer>
    ) : null;

    const urgencySignalNode =
        !footerMessageNode && urgencySignals?.messages ? (
            <UrgencySignalsNode
                urgencySignals={urgencySignals}
                floating={!isExpressCheckoutStabilized}
                isAppRebrandActive={isAppRebrandActive}
            />
        ) : null;

    const onPaneCloseIntent = () => {
        setIsPaneOpen(false);
    };

    const onQuantityChangeCallback = (data: onQuantityChangeFunctionParam) => {
        onQuantityChange(data);

        if (isThereGooglePayPaymentRequest) {
            refreshGooglePayPaymentRequest?.();
        }
    };

    const promoCodeForm = shouldShowPromoCodeForm ? (
        <div className={promoCodeWrapperClassNames}>
            <PromoCodeBar
                isAppRebrandActive={isAppRebrandActive}
                isRegEvent={isRegEvent}
                salesDateForAllTickets={salesDateForAllTickets}
                promoCodeFormOptions={promoCodeFormOptions}
                promoCodeFormHandlers={{
                    ...promoCodeFormHandlers,
                    onSubmit: _handleSubmitPromoCode,
                    onRemove: _handleNotificationClose,
                }}
                shouldShowPromoCodeForm={true}
                statusForAllTickets={statusForAllTickets}
                ticketsWithAppliedCode={ticketsWithAppliedCode?.length ?? 0}
                checkoutStep={TICKETS_FULL_SIZE_PATH}
            />
        </div>
    ) : null;

    const eventResellerNode = showEventReseller ? (
        <ResellerFooter
            resellerUrl={ticketReseller.url}
            resellerText={ticketReseller.text}
        />
    ) : null;

    const ctaTextProps = fixedFooterCtaText
        ? {
              ctaText: fixedFooterCtaText,
              isSubmitting: templateProps.isSubmitting,
              isSubmitDisabled: isDisabled,
              keepDisabledStyleWhileLoading: false,
          }
        : {
              ctaText: null,
          };

    const footerActionProps = modalFooterNode
        ? {
              modalFooterNode,
          }
        : ctaTextProps;

    const templateHeaderProps = getTemplateHeaderProps({
        hasAdvancedTeamsEnabled,
        onBackToGroupTypeSelection,
        onBackToSeries,
        shouldShowBackToSeriesLink,
        groupType,
    });
    const { title, secondaryTitle } = isSeriesInfiniteCalendar
        ? { title: '', secondaryTitle: '' }
        : { title: eventTitle, secondaryTitle: eventDate };
    const shouldHidePageFooter =
        isSeriesInfiniteCalendar && ctaText !== CTA_JOIN_WAITLIST
            ? !selectedTickets.length
            : false;

    return (
        <PageTemplate
            isAppRebrandActive={isAppRebrandActive}
            {...templateHeaderProps}
            {...footerActionProps}
            shouldHidePageFooter={shouldHidePageFooter}
            containerClassName={classNames({
                [styles.seriesInfiniteCalendarViewTemplateContainer]:
                    isSeriesInfiniteCalendar,
            })}
            wrapperClassname={classNames({
                [styles.seriesInfiniteCalendarViewTemplateWrapper]:
                    isSeriesInfiniteCalendar,
            })}
            dataSpec="checkout-widget-tickets-page"
            extraPaneProps={extraPaneProps}
            footerMessageNode={footerMessageNode}
            fullPageLayerNode={fullPageLoadingLayer}
            fullWidthTopContent={
                <LimitedPaymentMethodsInMobileNotification
                    isAppRebrandActive={isAppRebrandActive}
                />
            }
            handleSubmit={_onSubmit}
            hasTopMargin={false}
            isPaneOpen={isPaneOpen}
            isSubmitDisabled={isDisabled}
            notificationOptions={_getNotificationOptions()}
            offsetBottom={getFooterHeight()}
            onPaneCloseIntent={onPaneCloseIntent}
            onPaneToggle={_onPaneToggle}
            paneInteraction={paneInteraction}
            secondaryTitle={secondaryTitle}
            shouldAlignLeft={false}
            showCartSummaryButton={true}
            templateProps={templateProps}
            tertiaryNode={null}
            title={title}
            urgencySignalNode={urgencySignalNode}
            isExpressCheckout={isExpressCheckout}
            areTicketsSelected={
                getTotalSelectedTickets(selectedTickets || []) > 0
            }
            areOnlyFreeTicketsSelected={
                getTotalSelectedTickets(selectedTickets || []) > 0 &&
                allTicketsFree(selectedTickets || [])
            }
        >
            <div ref={focusRef} data-spec="focus-ref" />
            <div className={ticketsWrapperClassNames}>
                {promoCodeForm}
                <form
                    onSubmit={_onSubmit as any}
                    data-testid="ticket-selection-full-size-page-form"
                >
                    <WaitingRoomCTA
                        shouldShowCTA={shouldShowWaitingRoomCTA}
                        numWaitingRoomVisits={numWaitingRoomVisits}
                    />
                    {ticketClassCategories.map((groupKey, i) => (
                        <TicketCategoryGroup
                            key={i}
                            isAppRebrandActive={isAppRebrandActive}
                            isFormSubmitting={isFormSubmitting}
                            onDonationChange={onDonationChange}
                            onJoinWaitlist={onJoinWaitlist}
                            onQuantityChange={onQuantityChangeCallback}
                            promoCodeType={promoCodeFormOptions.promoCodeType}
                            shouldHideStatusOnTickets={shouldHideStatusOnTicket}
                            shouldShowTicketCategoryGroupHeaders={
                                shouldShowTicketCategoryGroupHeaders &&
                                !(
                                    isSeriesInfiniteCalendar &&
                                    groupKey === TicketClassCategory.admission
                                )
                            }
                            ticketClassGroupProps={
                                ticketClassesByCategory[groupKey]
                            }
                            ticketReseller={ticketReseller}
                            tickets={tickets}
                        />
                    ))}
                </form>
            </div>
            <LanguageAndBrand
                data-spec="language-and-brand-container-no-scroll"
                shouldShowLanguageLabel={!isSeriesInfiniteCalendar}
            />
            {eventResellerNode}
            <LoadingOverlay
                data-spec="spinner"
                size="large-thin"
                isShown={isLoading}
                style={getLoadingOverlayStyle(isAppRebrandActive)}
            />
        </PageTemplate>
    );
};

export default formValidate({
    form: TICKETS_PAGE_FORM_NAME,
    validate,
})(TicketSelectionFullSizePage);
